import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import SpecsTable from '../components/SpecsTable';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { MapPin, Eye, Calendar, Phone, Mail, Globe, Clock, Edit, Trash2, MessageSquare, ArrowLeft, User, Heart, Share2, ShoppingCart, ChevronRight } from 'lucide-react';
import ReviewSection from '../components/ReviewSection';
import ShareButtons from '../components/ShareButtons';
import { buildListingShareUrl } from '../lib/share';
import { setPageMeta, resetPageMeta } from '../lib/seo';
import { resolveAssetUrl } from '../lib/assets';
import { Breadcrumbs, breadcrumbJsonLd } from '../components/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const REGION_NAMES = {
  riga: 'Rīga', jurmala: 'Jūrmala', liepaja: 'Liepāja', ventspils: 'Ventspils',
  daugavpils: 'Daugavpils', rezekne: 'Rēzekne', jelgava: 'Jelgava', jekabpils: 'Jēkabpils',
  valmiera: 'Valmiera', ogre: 'Ogre', tukums: 'Tukums', talsi: 'Talsi', saldus: 'Saldus',
  kuldiga: 'Kuldīga', dobele: 'Dobele', bauska: 'Bauska', aizkraukle: 'Aizkraukle',
  aluksne: 'Alūksne', balvi: 'Balvi', cesis: 'Cēsis', gulbene: 'Gulbene', kraslava: 'Krāslava',
  limbazi: 'Limbaži', ludza: 'Ludza', madona: 'Madona', preili: 'Preiļi', sigulda: 'Sigulda',
  smiltene: 'Smiltene', valka: 'Valka', marupe: 'Mārupe', olaine: 'Olaine', salaspils: 'Salaspils', adazi: 'Ādaži'
};

const CATEGORY_COLORS = {
  shop: 'bg-[#D97736]',
  service: 'bg-[#4A6B53]',
  event: 'bg-[#6366f1]',
  project: 'bg-[#8b5cf6]'
};

// Top-down breadcrumb for a listing: Latvija › City › Category › Title.
const listingCrumbs = (l, label) => [
  { name: 'Latvija', to: '/karte' },
  { name: REGION_NAMES[l.region] || (l.region ? l.region.charAt(0).toUpperCase() + l.region.slice(1) : 'Latvija'), to: `/region/${l.region}` },
  { name: label(l.category), to: `/listings?region=${l.region}&category=${l.category}` },
  { name: l.title },
];

export default function ListingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  // Nearby local listings (same location_id, excluding current) — drives the
  // "Citi vietējie ražotāji" cross-sell strip.
  const [nearbyLocals, setNearbyLocals] = useState([]);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/listings/${id}`);
        setListing(data);
        // Per-page SEO meta + JSON-LD Product structured data
        const origin = window.location.origin;
        const slug = data.slug || data.id;
        const img = (data.images || [])[0];
        const absImg = img && img.startsWith('/') ? `${origin}${img}` : img;
        const priceStr = (typeof data.price === 'number')
          ? (data.price_type === 'free' ? 'Bezmaksas' : `€${data.price.toFixed(2)}`)
          : '';
        setPageMeta({
          title: priceStr ? `${data.title} — ${priceStr} | KoLV` : `${data.title} | KoLV`,
          description: (data.description || '').slice(0, 220),
          image: absImg,
          url: `${origin}/listing/${slug}`,
          type: 'website',
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Product",
              "name": data.title,
              "description": (data.description || '').slice(0, 500),
              "image": absImg || undefined,
              ...(typeof data.price === 'number' && data.price_type !== 'free' ? {
                "offers": { "@type": "Offer", "price": data.price, "priceCurrency": "EUR", "availability": "https://schema.org/InStock" }
              } : {}),
            },
            breadcrumbJsonLd(listingCrumbs(data, t), origin),
          ],
        });
      } catch (error) {
        console.error('Error fetching listing:', error);
        navigate('/listings');
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
    return () => { resetPageMeta(); };
  }, [id, navigate]);

  useEffect(() => {
    if (user && listing) {
      axios.get(`${API}/favorites/check/${id}`)
        .then(({ data }) => setIsFavorited(data.is_favorited))
        .catch(() => {});
    }
  }, [user, listing, id]);

  // Load nearby local listings — same micro-location, excluding self.
  useEffect(() => {
    if (!listing || !listing.location_id) {
      setNearbyLocals([]);
      return;
    }
    axios.get(`${API}/listings`, {
      params: { location_id: listing.location_id, limit: 8 },
    })
      .then(({ data }) => {
        const others = (Array.isArray(data) ? data : [])
          .filter((l) => l.id !== listing.id && l.is_active !== false)
          .slice(0, 6);
        setNearbyLocals(others);
      })
      .catch(() => setNearbyLocals([]));
  }, [listing, id]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/listings/${id}`);
      navigate('/my-listings');
    } catch (error) {
      console.error('Error deleting listing:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) return;
    try {
      const { data } = await axios.post(`${API}/favorites/${id}`);
      setIsFavorited(data.is_favorited);
    } catch (e) { console.error(e); }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !user) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: listing.owner_id,
        listing_id: listing.id,
        listing_title: listing.title,
        content: messageText
      });
      setMessageText('');
      setMessageSuccess(true);
      setTimeout(() => setMessageSuccess(false), 3000);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatPrice = () => {
    if (!listing.price && listing.price !== 0) return null;
    if (listing.price_type === 'free') return language === 'lv' ? 'Bezmaksas' : language === 'ru' ? 'Бесплатно' : 'Free';
    if (listing.price_type === 'negotiable') {
      if (listing.price > 0) return `€${listing.price} (${language === 'lv' ? 'pēc vienošanās' : language === 'ru' ? 'по договорённости' : 'negotiable'})`;
      return language === 'lv' ? 'Pēc vienošanās' : language === 'ru' ? 'По договорённости' : 'Negotiable';
    }
    if (listing.price_type === 'hourly') return `€${listing.price}/h`;
    return `€${listing.price}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]"></div>
      </div>
    );
  }

  if (!listing) return null;

  const isOwner = user && user._id === listing.owner_id;
  const defaultImage = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop';
  const images = listing.images?.length > 0 ? listing.images : [defaultImage];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="listing-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => {
            const idx = window.history.state?.idx;
            if (typeof idx === 'number' && idx > 0) navigate(-1);
            else navigate(listing?.region ? `/region/${listing.region}` : '/');
          }}
          className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-4 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}</span>
        </button>

        <Breadcrumbs items={listingCrumbs(listing, t)} className="mb-6" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden">
              <div className="relative aspect-[16/10]">
                <img
                  src={resolveAssetUrl(images[currentImageIndex])}
                  alt={listing.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = defaultImage; }}
                />
                <Badge
                  className={`absolute top-4 left-4 ${CATEGORY_COLORS[listing.category]} text-white border-0`}
                >
                  {t(listing.category)}
                </Badge>
                {/* Favorite button */}
                {user && (
                  <button
                    onClick={toggleFavorite}
                    className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isFavorited ? 'bg-red-500 text-white' : 'bg-white/80 backdrop-blur-sm text-[#5C5C5C] hover:text-red-500'
                    }`}
                    data-testid="favorite-btn"
                  >
                    <Heart className={`w-5 h-5 ${isFavorited ? 'fill-white' : ''}`} />
                  </button>
                )}
              </div>
              {images.length > 1 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-[#D97736]' : 'border-transparent'
                      }`}
                    >
                      <img src={resolveAssetUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
                  {listing.title}
                </h1>
                <ShareButtons
                  title={listing.title}
                  shareUrl={buildListingShareUrl(listing.slug || listing.id || listing._id)}
                />
              </div>

              <div className="flex flex-wrap gap-4 mb-6 text-sm text-[#5C5C5C]">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {REGION_NAMES[listing.region] || listing.region}
                    {listing.location_district ? ` · ${listing.location_district}` : (listing.location_name && listing.location_name !== (REGION_NAMES[listing.region] || listing.region) ? ` · ${listing.location_name}` : '')}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{listing.views} {t('views')}</span>
                </div>
                {listing.event_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(listing.event_date)}</span>
                  </div>
                )}
              </div>

              <div className="prose max-w-none">
                <p className="text-[#5C5C5C] whitespace-pre-wrap">
                  {listing.description}
                </p>
              </div>

              {listing.address && (
                <div className="mt-6 p-4 bg-[#F3F2EE] rounded-xl">
                  <h3 className="font-semibold text-[#121212] mb-1">{t('address')}</h3>
                  <p className="text-[#5C5C5C]">{listing.address}</p>
                </div>
              )}

              {/* Category-specific specifications */}
              <SpecsTable listing={listing} />
            </div>

            {/* Reviews */}
            <ReviewSection listingId={listing.id} ownerId={listing.owner_id} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Card */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              {formatPrice() && (
                <div className="mb-6">
                  <span className="text-sm text-[#5C5C5C]">{t('price')}</span>
                  <div className="text-3xl font-bold text-[#D97736] font-outfit">
                    {formatPrice()}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div className="space-y-3">
                {listing.contact_email && (
                  <a
                    href={`mailto:${listing.contact_email}`}
                    className="flex items-center gap-3 p-3 bg-[#F3F2EE] rounded-xl hover:bg-[#E5E4E0] transition-colors"
                  >
                    <Mail className="w-5 h-5 text-[#4A6B53]" />
                    <span className="text-[#121212] truncate">{listing.contact_email}</span>
                  </a>
                )}
                {listing.website && (
                  <a
                    href={listing.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#F3F2EE] rounded-xl hover:bg-[#E5E4E0] transition-colors"
                  >
                    <Globe className="w-5 h-5 text-[#4A6B53]" />
                    <span className="text-[#121212] truncate">{listing.website}</span>
                  </a>
                )}
              </div>

              {/* Add to Cart / Login to Buy — shown when there's a concrete price to charge.
                  "Negotiable" listings with a starting price still let buyers add to cart;
                  ones with price=0 fall through to the message-the-seller button below. */}
              {listing.price > 0 && listing.price_type !== 'free' && (
                <>
                  {!user ? (
                    <Button
                      onClick={() => navigate('/login')}
                      className="w-full mt-4 bg-[#4A6B53] hover:bg-[#3a5a43] text-white"
                      data-testid="login-to-buy-btn"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Ielogoties, lai pirktu' : language === 'ru' ? 'Войти, чтобы купить' : 'Login to Buy'}
                    </Button>
                  ) : !isOwner ? (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/cart/add`, { listing_id: listing.id, quantity: 1 });
                          toast.success(language === 'lv' ? 'Pievienots grozam!' : language === 'ru' ? 'Добавлено в корзину!' : 'Added to cart!', {
                            action: { label: language === 'lv' ? 'Uz grozu' : language === 'ru' ? 'В корзину' : 'View cart', onClick: () => navigate('/cart') },
                          });
                        } catch (e) {
                          console.error(e);
                          toast.error(language === 'lv' ? 'Neizdevās pievienot grozam' : language === 'ru' ? 'Не удалось добавить в корзину' : 'Could not add to cart');
                        }
                      }}
                      className="w-full mt-4 bg-[#4A6B53] hover:bg-[#3a5a43] text-white"
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Pievienot grozam' : language === 'ru' ? 'Добавить в корзину' : 'Add to Cart'}
                    </Button>
                  ) : null}
                </>
              )}

              {/* Message Button */}
              {user && !isOwner && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      className="w-full mt-4 bg-[#D97736] hover:bg-[#C06528] text-white"
                      data-testid="send-message-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t('sendMessage')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('sendMessage')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {messageSuccess ? (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-center">
                          {language === 'lv' ? 'Ziņa nosūtīta!' : language === 'ru' ? 'Сообщение отправлено!' : 'Message sent!'}
                        </div>
                      ) : (
                        <>
                          <Textarea
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            placeholder={t('typeMessage')}
                            rows={4}
                            className="resize-none"
                            data-testid="message-textarea"
                          />
                          <Button
                            onClick={handleSendMessage}
                            disabled={sendingMessage || !messageText.trim()}
                            className="w-full bg-[#D97736] hover:bg-[#C06528] text-white"
                            data-testid="submit-message-btn"
                          >
                            {sendingMessage ? t('loading') : t('sendMessage')}
                          </Button>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Owner Actions */}
              {isOwner && (
                <div className="mt-4 space-y-2">
                  <Link to={`/edit-listing/${listing.id}`}>
                    <Button
                      variant="outline"
                      className="w-full border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white"
                      data-testid="edit-listing-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {t('edit')}
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        data-testid="delete-listing-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('delete')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {language === 'lv' ? 'Dzēst sludinājumu?' : language === 'ru' ? 'Удалить объявление?' : 'Delete listing?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {language === 'lv'
                            ? 'Šī darbība ir neatgriezeniska. Sludinājums tiks neatgriezeniski dzēsts.'
                            : language === 'ru'
                            ? 'Это действие необратимо. Объявление будет удалено навсегда.'
                            : 'This action cannot be undone. The listing will be permanently deleted.'}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-red-500 hover:bg-red-600"
                          data-testid="confirm-delete-btn"
                        >
                          {t('delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>

            {/* Seller Card */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-[#121212] mb-4">
                {language === 'lv' ? 'Pārdevējs' : language === 'ru' ? 'Продавец' : 'Seller'}
              </h3>
              <Link
                to={`/shop/${listing.owner_id}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                data-testid="seller-link"
              >
                <div className="w-12 h-12 rounded-full bg-[#4A6B53] flex items-center justify-center text-white text-lg font-semibold">
                  {listing.owner_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-[#121212]">{listing.owner_name}</p>
                  <p className="text-sm text-[#5C5C5C]">
                    {language === 'lv' ? 'Skatīt profilu' : language === 'ru' ? 'Смотреть профиль' : 'View profile'}
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Nearby local producers in the same micro-location */}
        {nearbyLocals.length > 0 && (
          <div className="mt-10 pt-8 border-t border-[#E5E4E0]" data-testid="nearby-locals-section">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
              <div>
                <h2 className="font-outfit font-bold text-2xl text-[#121212]">
                  {language === 'lv'
                    ? (listing.location_name ? `Citi vietējie ražotāji · ${listing.location_name}` : 'Citi vietējie ražotāji')
                    : language === 'ru'
                    ? (listing.location_name ? `Другие местные производители · ${listing.location_name}` : 'Другие местные производители')
                    : `Other locals${listing.location_name ? ` · ${listing.location_name}` : ''}`}
                </h2>
                <p className="text-sm text-[#5C5C5C] mt-1">
                  {language === 'lv'
                    ? 'Atbalsti savus tuvākos kaimiņus — visi šie sludinājumi ir no tās pašas mikroreģionijas.'
                    : language === 'ru'
                    ? 'Поддержите ближайших соседей — все эти объявления из того же микрорайона.'
                    : 'Support your closest neighbours — all from the same micro-region.'}
                </p>
              </div>
              {listing.location_name && (
                <Link
                  to={`/region/${(listing.location_name || '').toLowerCase()}`}
                  className="text-sm font-medium text-[#D97736] hover:text-[#C06528] flex items-center gap-1"
                  data-testid="nearby-locals-view-all"
                >
                  {language === 'lv' ? 'Skatīt visus' : language === 'ru' ? 'Смотреть все' : 'View all'}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {nearbyLocals.map((other) => (
                <Link
                  key={other.id}
                  to={`/listing/${other.slug || other.id}`}
                  className="group bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden hover:border-[#D97736] hover:shadow-md transition-all"
                  data-testid={`nearby-local-${other.id}`}
                >
                  <div className="aspect-square bg-[#F3F2EE] overflow-hidden">
                    {(other.images || [])[0] ? (
                      <img
                        src={other.images[0]}
                        alt={other.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#5C5C5C] text-3xl">
                        🌿
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-medium text-[#121212] line-clamp-2 min-h-[2.5rem]">
                      {other.title}
                    </p>
                    {typeof other.price === 'number' && (
                      <p className="text-sm font-bold text-[#D97736] mt-1">€{other.price.toFixed(2)}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
